//#region [IMPORT]

// === THIRD-PARTY ===
import React, { useState, useEffect, useRef, useEffectEvent } from 'react';

// === STYLE / MATERIAL UI ===
import { useTheme } from '@mui/material/styles';

// === COMPONENTS ===
import { Thumbnail } from './components/Thumbnail/Thumbnail.jsx';
import Styled from 'components/Styled/Styled.jsx';
import Collapsible, { Header } from 'components/Collapsible';

// === HOOKS ===
import { useFile } from '../FileContext/FileContext.jsx';
import useOnVisible from 'components/hooks/useOnVisible/useOnVisible.js';

// === API ===
import {emit, on, removeListener, sendMessageData} from 'api/prokey/prokey.websocket.js';

//#endregion

const ThumbDocMemo = React.memo( ThumbDocument )
const ThumbMemo = React.memo( Thumbnail )

export function ThumbPanel(props) {

  //#region [VALUES]

  const { is_comp } = props;

  const theme = useTheme();

  const { file_id, is_loading } = useFile();

  const [ documents, setDocuments ] = useState([]);

  const ref = useRef();

  let _scroll_throttle = false;

  //#endregion

  //#region [STYLES]

  const scrollbar_thin = {
    // scrollbarColor : `${theme.palette.primary.main} ${theme.palette.background.secondary[theme.palette.mode]}`,
    scrollbarWidth : 'thin'
  }

  //#endregion
  
  //#region [EFFECT]
  
  useEffect( () => {
    if( is_loading ) return;

    const DOCUMENTS = is_comp ? 'FILE_COMPARISON_DOCUMENTS' : 'FILE_DOCUMENTS';
    
    //-----------------------------------------------------------------
    function handleDocuments( data ) {
      //-----------------------------------------------------------------
      setDocuments( data );
    }

    on( DOCUMENTS, handleDocuments );

    const request_type = is_comp ? 'FILE_REQUEST_COMPARISON_DOCUMENTS' : 'FILE_REQUEST_DOCUMENTS';
    sendMessageData( request_type, { file_id } );

    return () => {
      removeListener( DOCUMENTS, handleDocuments );
    }
  }, [file_id, is_loading, is_comp] )
  
  useEffect( () => {
    if( ref?.current == undefined ) return;
    ref.current.addEventListener( 'wheel', onWheel, { passive: false } );

    return () => {
      if( ref.current ) ref.current.removeEventListener( 'wheel', onWheel, { passive: false } );
    }
  }, [ref.current] )

  //#endregion

  //#region [EVENTS]

  function onMouseUp() {
    emit( 'FILE_FIELD_FOCUS' );
  }
  
  function onWheel(event) {
    event.preventDefault();
    if (!_scroll_throttle) {
      setTimeout(() => {
        if( event.deltaY > 0 ) {
          sendMessageData( 'FILE_IMAGE_SELECT_NEXT', { file_id: file_id } );
        } else if( event.deltaY < 0 ) {
          sendMessageData( 'FILE_IMAGE_SELECT_PREV', { file_id: file_id } );
        }
        _scroll_throttle = false;
      }, 50);

      _scroll_throttle = true;
    }
  };

  //#endregion

  //#region [RENDER]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    return (
      <Styled ref={ref} styles={[{ width : '100%' , height : 'calc( 100% )' }, { position : 'static' },{overflow:'auto'},scrollbar_thin]} onMouseUp={onMouseUp}>
        {displayDocuments()}
      </Styled>
    )
  }

  //-----------------------------------------------------------------
  function displayDocuments() {
    //-----------------------------------------------------------------
    if( documents == null ) return [];

    const result = [];

    for ( let doc of documents ) {
      result.push( <ThumbDocMemo key={doc.id} id={doc.id} is_comp={is_comp} /> );
    }

    return result;
  }

  //#endregion
  
  return display();
}


export function ThumbDocument(props) {

  //#region [VALUES]
 
  const { id, is_comp } = props;

  const theme = useTheme();

  const { file_id } = useFile();

  const [ document, setDocument ] = useState([]);
  const [  focused, setFocused  ] = useState(false);
  
  const ref = useRef(null);
  const [is_visible] = useOnVisible(ref)

  //#endregion

  //#region [STYLES]

  //#endregion
  
  //#region [EFFECT]
  
  useEffect( () => {
    const DOCUMENT = is_comp ? 'FILE_COMPARISON_DOCUMENT' : 'FILE_DOCUMENT';
    
    //-----------------------------------------------------------------
    function handleDocument( data ) {
      //-----------------------------------------------------------------
      if( id !== data.id ) return;
      setDocument( data );
    }

    on( DOCUMENT, handleDocument );

    return () => {
      removeListener( DOCUMENT, handleDocument );
    }
  }, [] )
  
  useEffect( () => {
    function handleFocus(data) {
      if( data.id !== id && focused ) {
        setFocused( false );
      };
      if( data.id !== id ) return;
      setFocused( true );
    }

    const type = is_comp ? 'FILE_COMPARISON_DOCUMENT_FOCUS' : 'FILE_DOCUMENT_FOCUS';
    on( type, handleFocus );

    return () => {
      removeListener( type, handleFocus );
    }
  }, [id, focused] );
    
  useEffect( () => {
    if( !is_visible ) return;
    const type = is_comp ? 'FILE_REQUEST_COMPARISON_DOCUMENT' : 'FILE_REQUEST_DOCUMENT';
    sendMessageData( type, { file_id, document_id: id } );
  }, [id,is_visible] )

  //#endregion

  //#region [RENDER]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    const body = displayPages(document.pages)
    const color = is_comp ? theme.palette.secondary.main : '#ffa500';
    const bg = focused ? { background: color } : { background: theme.palette.primary.main }
    
    return (
      <Collapsible
        ref={ref}
        key={id}
        styles={ [{height: 'fit-content'}]}
        header        = {{ children: <Header title={document.name} styles={[ {fontWeight: 'bold'} ]} />, styles:[bg, { color: theme.palette.primary.contrastText }] }}
        header_closed = {{ children: <Header title={document.name} styles={[ {fontWeight: 'bold'} ]} />, styles:[bg, { color: theme.palette.primary.contrastText }] }}
        body          = {{
          children: body,
          styles: [{height: 'fit-content'} ]
        }}
        body_closed   = {{ children: null }}
      />
    )
  }

  //-----------------------------------------------------------------
  function displayPages(pages) {
    //-----------------------------------------------------------------
    if( pages == null ) return null;

    const thumbs = pages.map( id => <ThumbMemo key={id} id={id} is_comp={is_comp} /> );

    return (
      <Styled styles={[{display:'flex'},{ flexWrap: 'wrap' }, { paddingTop: '8px' } ]}>
        {document == null || !is_visible? (
           <div>Loading...</div> 
          ) : thumbs}
      </Styled>
    );
  }

  //#endregion
  
  return display();
}